import chr from "chroma-js";
import { format } from "date-fns";
import { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { IconCheck, IconEllipsis, IconMinus, IconSparks, IconFolder, IconPlus } from "@cynaps/icons";
import { Userpic, Button, Dropdown, Tooltip } from "@cynaps/ui";
import { Menu, Pagination } from "../../components";
import { useAuth } from "@cynaps/core/providers/AuthProvider";
import { cn } from "../../utils/bem";
import { ProjectStateChip } from "@cynaps/app-common";

const DEFAULT_CARD_COLORS = ["#FFFFFF", "#FDFDFC"];

// Circular progress ring component
const CircularProgress = ({ percentage, size = 80, strokeWidth = 6 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(139, 92, 246, 0.15)"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#8b5cf6"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="square"
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
          fontSize: "16px",
          fontWeight: 700,
          color: "#c4b5fd",
          textAlign: "center",
          lineHeight: 1,
        }}
      >
        {Math.round(percentage)}%
      </div>
    </div>
  );
};

const primaryButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  marginTop: "24px",
  padding: "0 24px",
  height: "40px",
  minWidth: "90px",
  background: "#8b5cf6",
  border: "1px solid #8b5cf6",
  borderRadius: "0px",
  color: "#ffffff",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxShadow: "0 0 16px rgba(139, 92, 246, 0.35)",
};

// Stat badge component
const StatBadge = ({ icon: Icon, value, label, color, gradient }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        background: "#000000",
        border: `1px solid ${color}40`,
        borderRadius: "0px",
        transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "default",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
          height: "24px",
          borderRadius: "0px",
          background: `${color}18`,
          color: color,
        }}
      >
        <Icon style={{ width: "14px", height: "14px" }} />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        <span
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
            fontSize: "15px",
            fontWeight: 700,
            color: color,
            lineHeight: 1,
          }}
        >
          {value}
        </span>
        <span
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
            fontSize: "10px",
            fontWeight: 600,
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            lineHeight: 1,
          }}
        >
          {label}
        </span>
      </div>
    </div>
  );
};

const CreateProjectCard = ({ onClick }) => {
  return (
    <div
      onClick={onClick}
      className={cn("project-card").mod({ create: true }).toClassName()}
      role="button"
      tabIndex={0}
    >
      <div className={cn("project-card").elem("create-content").toClassName()}>
        <div className={cn("project-card").elem("create-icon-wrapper").toClassName()}>
          <IconPlus style={{ width: "24px", height: "24px" }} />
        </div>
        <span className={cn("project-card").elem("create-text").toClassName()}>
          Create New Project
        </span>
      </div>
    </div>
  );
};

export const ProjectsList = ({ projects, currentPage, totalItems, loadNextPage, pageSize, openModal }) => {
  const { user } = useAuth();
  const isAnnotator = !!user?.is_annotator;
  const isExpert = !!user?.is_expert;
  // Both annotators and experts see simplified view
  const role = isAnnotator || isExpert ? "worker" : "client";

  return (
    <>
      <div className={cn("projects-page").elem("list").toClassName()}>
        {openModal && <CreateProjectCard onClick={openModal} />}
        {projects.map((project) => (
          <ProjectCard key={project.id} project={project} role={role} />
        ))}
      </div>
      <div className={cn("projects-page").elem("pages").toClassName()}>
        <Pagination
          name="projects-list"
          label="Projects"
          page={currentPage}
          totalItems={totalItems}
          urlParamName="page"
          pageSize={pageSize}
          pageSizeOptions={[10, 30, 50, 100]}
          onPageLoad={(page, pageSize) => loadNextPage(page, pageSize)}
        />
      </div>
    </>
  );
};

export const EmptyProjectsList = ({ openModal, isAnnotator, isExpert }) => {
  const isWorker = isAnnotator || isExpert;

  return (
    <div className={cn("empty-projects-page").toClassName()}>
      <div className={cn("empty-projects-page").elem("icon").toClassName()}>
        <IconFolder />
      </div>
      <div className={cn("empty-projects-page").elem("step-indicator").toClassName()}>01/</div>
      {isWorker ? (
        <>
          <h1 className={cn("empty-projects-page").elem("header").toClassName()}>No projects assigned</h1>
          <p>You don't have any projects assigned yet.</p>
        </>
      ) : (
        <>
          <h1 className={cn("empty-projects-page").elem("header").toClassName()}>Create your first project</h1>
          <p>Import your data and configure the labeling interface to start annotating.</p>
          {openModal && (
            <button
              onClick={openModal}
              className="create-btn"
              aria-label="Create new project"
              style={primaryButtonStyle}
            >
              Create Project
            </button>
          )}
        </>
      )}
    </div>
  );
};

const ProjectCard = ({ project, role }) => {
  const color = useMemo(() => {
    return DEFAULT_CARD_COLORS.includes(project.color) ? null : project.color;
  }, [project]);

  const projectColors = useMemo(() => {
    const textColor =
      color && chr(color).luminance() > 0.3
        ? "var(--color-neutral-inverted-content)"
        : "var(--color-neutral-inverted-content)"; // Determine text color based on luminance
    return color
      ? {
          "--header-color": color,
          "--background-color": chr(color).alpha(0.2).css(),
          "--text-color": textColor,
          "--border-color": chr(color).alpha(0.5).css(),
        }
      : {};
  }, [color]);

  return (
    <NavLink
      className={cn("projects-page").elem("link").toClassName()}
      to={`/projects/${project.id}/data`}
      data-external
    >
      <div className={cn("project-card").mod({ colored: !!color }).toClassName()} style={projectColors}>
        <div className={cn("project-card").elem("header").toClassName()}>
          <div className={cn("project-card").elem("title").toClassName()}>
            <div className={cn("project-card").elem("title-text-wrapper").toClassName()}>
              <Tooltip title={project.title ?? "New project"}>
                <div className={cn("project-card").elem("title-text").toClassName()}>
                  {project.title ?? "New project"}
                </div>
              </Tooltip>
            </div>

            {role !== "worker" && (
              <div
                className={cn("project-card").elem("menu").toClassName()}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <Dropdown.Trigger
                  content={
                    <Menu contextual>
                      <Menu.Item href={`/projects/${project.id}/settings`}>Settings</Menu.Item>
                      <Menu.Item href={`/projects/${project.id}/data?labeling=1`}>Label</Menu.Item>
                    </Menu>
                  }
                >
                  <Button size="smaller" look="string" aria-label="Project options">
                    <IconEllipsis />
                  </Button>
                </Dropdown.Trigger>
              </div>
            )}

            {role === "worker" && project.state && (
              <ProjectStateChip state={project.state} projectId={project.id} interactive={false} />
            )}
          </div>
          <div className={cn("project-card").elem("summary").toClassName()}>
            <div
              className={cn("project-card").elem("annotation").toClassName()}
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "center",
                padding: "16px 0",
              }}
            >
              {/* Circular Progress for Task Completion */}
              <div style={{ flexShrink: 0 }}>
                <CircularProgress
                  percentage={(() => {
                    // For annotators/experts: use num_tasks_with_annotations (completed) / task_number (total)
                    if (role === "worker") {
                      const total = project.task_number ?? 0;
                      const completed = project.num_tasks_with_annotations ?? 0;
                      return total > 0 ? (completed / total) * 100 : 0;
                    }
                    // For clients: use overall project stats
                    return project.task_number > 0 ? (project.finished_task_number / project.task_number) * 100 : 0;
                  })()}
                  size={80}
                  strokeWidth={6}
                />
                <div
                  style={{
                    marginTop: "8px",
                    textAlign: "center",
                    fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#9ca3af",
                  }}
                >
                  {role === "worker"
                    ? `${project.num_tasks_with_annotations ?? 0} / ${project.task_number ?? 0}`
                    : `${project.finished_task_number ?? 0} / ${project.task_number ?? 0}`}
                  <div
                    style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: "2px", color: "#6b7280" }}
                  >
                    Tasks Done
                  </div>
                </div>
              </div>

              {/* Stats Grid - Simplified for workers (annotators/experts) */}
              <div
                style={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: role === "worker" ? "1fr" : "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: "8px",
                }}
              >
                {role === "worker" ? (
                  <StatBadge
                    icon={IconCheck}
                    value={project.num_tasks_with_annotations ?? 0}
                    label="Completed"
                    color="#10b981"
                  />
                ) : (
                  <>
                    <StatBadge
                      icon={IconCheck}
                      value={project.total_annotations_number ?? 0}
                      label="Annotated"
                      color="#10b981"
                    />
                    <StatBadge
                      icon={IconMinus}
                      value={project.skipped_annotations_number ?? 0}
                      label="Skipped"
                      color="#f59e0b"
                    />
                    <StatBadge
                      icon={IconSparks}
                      value={project.total_predictions_number ?? 0}
                      label="Predictions"
                      color="#a78bfa"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={cn("project-card").elem("description").toClassName()}>
          {project.description || (
            <span style={{ color: "var(--color-neutral-subtle)", fontStyle: "italic" }}>No description</span>
          )}
        </div>
        <div className={cn("project-card").elem("info").toClassName()}>
          <div className={cn("project-card").elem("created-date").toClassName()}>
            {format(new Date(project.created_at), "dd MMM yy, HH:mm")}
          </div>
          {role !== "worker" && project.created_by && (
            <div className={cn("project-card").elem("created-by").toClassName()}>
              <Userpic src={project.created_by.avatar} user={project.created_by} showUsernameTooltip />
              {(project.created_by.first_name || project.created_by.last_name || project.created_by.email) && (
                <span className={cn("project-card").elem("creator-name").toClassName()}>
                  {project.created_by.first_name && project.created_by.last_name
                    ? `${project.created_by.first_name} ${project.created_by.last_name}`
                    : project.created_by.first_name || project.created_by.last_name || project.created_by.email}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </NavLink>
  );
};
